import React, { useEffect, useContext, useState, useMemo } from "react";
import { ProductsContext } from "../context/ProductsContext";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { API_BASE_URL } from "../config/api";
import { brandModelData } from "../context/brandModelData";
import "./catalog-wow.css";


const Shop = () => {
  const { products = [], fetchProducts, loading, error } =
    useContext(ProductsContext);

  const { lang } = useLanguage();
  const navigate = useNavigate();

  const ALL = "All";
  const PRODUCTS_PER_PAGE = 18;

  const [currentPage, setCurrentPage] = useState(1);

  const [selectedCategory, setSelectedCategory] = useState(ALL);
  const [selectedCondition, setSelectedCondition] = useState(ALL);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 0]);
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // ================= FETCH =================
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // ================= RESET PAGE =================
  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedCategory,
    selectedCondition,
    selectedBrands,
    selectedColors,
    priceRange,
    search,
  ]);

  // ================= TRANSLATIONS =================
  const t = {
    sq: {
      search: "Kërko produkt...",
      price: "Çmimi",
      max: "Maksimumi",
      category: "Kategoria",
      brand: "Marka",
      color: "Ngjyra",
      products: "Produktet",
      reset: "Pastro filtrat",
      view: "Shiko",
      filters: "Filtrat",
      closeFilters: "Mbyll Filtrat",
      noResults: "Nuk u gjet asnjë produkt.",
    },
    en: {
      search: "Search product...",
      price: "Price",
      max: "Max",
      category: "Category",
      brand: "Brand",
      color: "Color",
      products: "Products",
      reset: "Reset filters",
      view: "View",
      filters: "Filters",
      closeFilters: "Close Filters",
      noResults: "No products found.",
    },
  };

  const text = t[lang] || t.en;

  const categoryTranslations = {
  Telefona: {
    sq: "Telefona",
    en: "Phones",
  },

  Laptop: {
    sq: "Laptop",
    en: "Laptops",
  },

  Tablete: {
    sq: "Tablete",
    en: "Tablets",
  },

  Kufje: {
    sq: "Kufje",
    en: "Headphones",
  },

  Smartwatch: {
    sq: "Smartwatch",
    en: "Smart Watches",
  },

  Aksesore: {
    sq: "Aksesorë",
    en: "Accessories",
  },
};

  // ================= SAFE PRODUCTS =================
  const safeProducts = useMemo(() => {
    if (!Array.isArray(products)) {
      return [];
    }

    return products.filter(
      (product) =>
        Number(product?.is_published) === 1
    );
  }, [products]);

  // ================= PRICE HELPERS =================
  const getNormalPrice = (product) =>
    Number(product?.price || 0);

  const getDiscountPercent = (product) =>
    Number(product?.discount || 0);

  const hasActiveOffer = (product) =>
    Number(product?.is_offer) === 1 &&
    getDiscountPercent(product) > 0;

  const getDiscountedPrice = (product) => {
    const normalPrice =
      getNormalPrice(product);

    if (!hasActiveOffer(product)) {
      return normalPrice;
    }

    return (
      normalPrice *
      (1 - getDiscountPercent(product) / 100)
    );
  };

  const getEffectivePrice = (product) =>
    hasActiveOffer(product)
      ? getDiscountedPrice(product)
      : getNormalPrice(product);

  // ================= DYNAMIC MAX PRICE =================
  const maxProductPrice = useMemo(() => {
    if (!safeProducts.length) {
      return 500000;
    }

    const highestEffectivePrice =
      Math.max(
        ...safeProducts.map((product) =>
          getEffectivePrice(product)
        )
      );

    return Math.max(
      1000,
      Math.ceil(
        highestEffectivePrice / 1000
      ) * 1000
    );
  }, [safeProducts]);

  useEffect(() => {
    setPriceRange((previous) => {
      const currentMax =
        Number(previous?.[1] || 0);

      if (
        currentMax === 0 ||
        currentMax < maxProductPrice
      ) {
        return [
          0,
          maxProductPrice,
        ];
      }

      return previous;
    });
  }, [maxProductPrice]);

  // ================= COLORS =================
  const colorMap = {
    Zeze: "#000000",
    Bardhe: "#ffffff",
    Gri: "#9ca3af",
    Blu: "#2563eb",
    Jeshile: "#16a34a",
    Kuqe: "#dc2626",
    Verdhe: "#eab308",
    Vjollce: "#a855f7",
    Rozë: "#ec4899",
    Ari: "#d4af37",
    Argjend: "#c0c0c0",
  };

  const colorList = Object.keys(colorMap);

  // ================= CATEGORIES =================
const categoryList = useMemo(() => {
  const categories = [
    ...new Set(
      safeProducts
        .map((p) => p.category)
        .filter(Boolean)
    ),
  ];

  return [ALL, ...categories];
}, [safeProducts]);

  // ================= BRANDS =================
  const brandList = useMemo(() => {
    const source =
      selectedCategory === ALL
        ? safeProducts
        : safeProducts.filter(
            (product) =>
              product.category ===
              selectedCategory
          );

    const productBrands = [
      ...new Set(
        source
          .map((product) => product.brand)
          .filter(Boolean)
      ),
    ].sort();

    if (productBrands.length) {
      return productBrands;
    }

    if (selectedCategory === ALL) {
      return [
        ...new Set(
          Object.values(
            brandModelData || {}
          ).flatMap((category) =>
            Object.keys(category || {})
          )
        ),
      ].sort();
    }

    return Object.keys(
      brandModelData?.[
        selectedCategory
      ] || {}
    ).sort();
  }, [
    safeProducts,
    selectedCategory,
  ]);

  // ================= IMAGE =================
  const getImage = (img) => {
    if (!img) {
      return "/placeholder.png";
    }

    let imageValue = img;

    if (Array.isArray(imageValue)) {
      imageValue = imageValue[0];
    }

    if (typeof imageValue === "string") {
      const trimmed = imageValue.trim();

      if (
        trimmed.startsWith("[") &&
        trimmed.endsWith("]")
      ) {
        try {
          const parsed = JSON.parse(trimmed);

          if (
            Array.isArray(parsed) &&
            parsed.length
          ) {
            imageValue = parsed[0];
          }
        } catch {
          imageValue = trimmed;
        }
      }

      if (
        typeof imageValue === "string" &&
        imageValue.includes(",")
      ) {
        imageValue =
          imageValue.split(",")[0];
      }
    }

    const cleanImage =
      String(imageValue || "").trim();

    if (!cleanImage) {
      return "/placeholder.png";
    }

    if (
      cleanImage.startsWith("http://") ||
      cleanImage.startsWith("https://")
    ) {
      return cleanImage;
    }

    if (
      cleanImage.startsWith("/uploads/")
    ) {
      return `${API_BASE_URL}${cleanImage}`;
    }

    if (
      cleanImage.startsWith("uploads/")
    ) {
      return `${API_BASE_URL}/${cleanImage}`;
    }

    return `${API_BASE_URL}/uploads/${cleanImage}`;
  };

  // ================= FILTER ENGINE =================
  const filteredProducts = useMemo(() => {
    const norm = (v) => (v || "").toLowerCase().trim();

    return safeProducts.filter((p) => {
      const matchCategory =
        selectedCategory === ALL || p.category === selectedCategory;

      const matchCondition =
        selectedCondition === ALL ||
        norm(p.product_condition) === norm(selectedCondition);

      const matchBrand =
        selectedBrands.length === 0 || selectedBrands.includes(p.brand);

      const matchColor =
        selectedColors.length === 0 || selectedColors.includes(p.color);

      const effectivePrice =
        getEffectivePrice(p);

      const matchPrice =
        effectivePrice >=
          Number(priceRange[0] || 0) &&
        effectivePrice <=
          Number(
            priceRange[1] ||
              maxProductPrice
          );

      const matchSearch =
        !search ||
        p.name?.toLowerCase().includes(search.toLowerCase());

      return (
        matchCategory &&
        matchCondition &&
        matchBrand &&
        matchColor &&
        matchPrice &&
        matchSearch
      );
    });
  }, [
    safeProducts,
    selectedCategory,
    selectedCondition,
    selectedBrands,
    selectedColors,
    priceRange,
    search,
  ]);

  const translateCategory = (category) => {
  if (!category) return "";

  return (
    categoryTranslations[category]?.[lang] ||
    category
  );
};

  // ================= PAGINATION =================
  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE)
  );

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  // ================= LOADING / ERROR =================
  if (loading) {
    return (
      <div className="p-10 text-center">
        {lang === "sq"
          ? "Duke ngarkuar produktet..."
          : "Loading products..."}
      </div>
    );
  }

  if (error) {
    return <div className="p-10 text-center text-red-500">{error}</div>;
  }

  // ================= UI =================
return (
  <div className="min-h-screen bg-slate-50 px-3 py-4 md:px-5">
    {/* ================= MOBILE FILTER BUTTON ================= */}
    <div className="mb-4 md:hidden">
      <button
        type="button"
        onClick={() => setShowFilters(!showFilters)}
        className="
          w-full
          rounded-xl
          bg-blue-600
          py-3
          text-sm
          font-bold
          text-white
          shadow-lg
          shadow-blue-200/70
          transition
          hover:bg-blue-700
        "
      >
        {showFilters ? text.closeFilters : text.filters}
      </button>
    </div>

    {/* ================= MAIN LAYOUT ================= */}
    <div
      className="
        mx-auto
        flex
        w-full
        max-w-[1800px]
        flex-col
        items-start
        gap-5
        md:flex-row
      "
    >
      {/* ================= FILTERS ================= */}
      <aside
        className={`
          h-fit
          w-full
          self-start
          rounded-2xl
          border
          border-slate-200
          bg-white
          p-5
          shadow-[0_12px_35px_rgba(15,23,42,0.08)]

          md:sticky
          md:top-16
          md:w-60
          md:min-w-60

          ${showFilters ? "block" : "hidden"}
          md:block
        `}
      >
        {/* SEARCH */}
        <input
          type="text"
          className="
            mb-4
            h-11
            w-full
            rounded-xl
            border
            border-slate-200
            bg-slate-50
            px-3
            text-sm
            text-slate-800
            outline-none
            transition
            placeholder:text-slate-400
            focus:border-blue-500
            focus:bg-white
            focus:ring-4
            focus:ring-blue-100
          "
          placeholder={text.search}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* ================= PRICE ================= */}
        <div className="border-b border-slate-100 pb-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-extrabold text-slate-900">
              {text.price}
            </h3>

            <span className="text-xs font-bold text-blue-600">
              {Number(priceRange[1]).toLocaleString()} ALL
            </span>
          </div>

          <input
            type="range"
            min="0"
            max={maxProductPrice}
            value={priceRange[1]}
            onChange={(e) =>
              setPriceRange([0, Number(e.target.value)])
            }
            className="w-full cursor-pointer accent-blue-600"
          />

          <div className="mt-2 flex justify-between text-xs text-slate-500">
            <span>0 ALL</span>
            <span>{text.max}</span>
          </div>
        </div>

        {/* ================= CATEGORY ================= */}
        <div className="border-b border-slate-100 py-5">
          <h3 className="mb-2 text-sm font-extrabold text-slate-900">
            {text.category}
          </h3>

          <div className="space-y-1">
            {categoryList.map((c) => (
              <label
                key={c}
                className="
                  flex
                  min-h-8
                  cursor-pointer
                  items-center
                  gap-2
                  rounded-lg
                  px-2
                  text-sm
                  font-medium
                  text-slate-600
                  transition
                  hover:bg-blue-50
                  hover:text-blue-700
                "
              >
                <input
                  type="radio"
                  checked={selectedCategory === c}
                  onChange={() => setSelectedCategory(c)}
                  className="
                    h-4
                    w-4
                    cursor-pointer
                    accent-blue-600
                  "
                />

                <span>{translateCategory(c)}</span>
              </label>
            ))}
          </div>
        </div>

        {/* ================= BRAND ================= */}
        <div className="border-b border-slate-100 py-5">
          <h3 className="mb-2 text-sm font-extrabold text-slate-900">
            {text.brand}
          </h3>

          <div className="max-h-44 space-y-1 overflow-y-auto pr-1">
            {brandList.map((b) => (
              <label
                key={b}
                className="
                  flex
                  min-h-8
                  cursor-pointer
                  items-center
                  gap-2
                  rounded-lg
                  px-2
                  text-sm
                  font-medium
                  text-slate-600
                  transition
                  hover:bg-blue-50
                  hover:text-blue-700
                "
              >
                <input
                  type="checkbox"
                  checked={selectedBrands.includes(b)}
                  onChange={() =>
                    setSelectedBrands((prev) =>
                      prev.includes(b)
                        ? prev.filter((x) => x !== b)
                        : [...prev, b]
                    )
                  }
                  className="
                    h-4
                    w-4
                    cursor-pointer
                    rounded
                    accent-blue-600
                  "
                />

                <span>{b}</span>
              </label>
            ))}
          </div>
        </div>

        {/* ================= COLORS ================= */}
        <div className="pt-5">
          <h3 className="mb-3 text-sm font-extrabold text-slate-900">
            {text.color}
          </h3>

          <div className="flex flex-wrap gap-3">
            {colorList.map((c) => (
              <button
                type="button"
                key={c}
                title={c}
                aria-label={c}
                onClick={() =>
                  setSelectedColors((prev) =>
                    prev.includes(c)
                      ? prev.filter((x) => x !== c)
                      : [...prev, c]
                  )
                }
                className={`
                  h-7
                  w-7
                  cursor-pointer
                  rounded-full
                  border-2
                  border-white
                  shadow-[0_0_0_1px_#cbd5e1]
                  transition
                  hover:scale-110

                  ${
                    selectedColors.includes(c)
                      ? "ring-2 ring-blue-600 ring-offset-2"
                      : ""
                  }
                `}
                style={{ backgroundColor: colorMap[c] }}
              />
            ))}
          </div>
        </div>

        {/* ================= RESET ================= */}
        <button
          type="button"
          onClick={() => {
            setSelectedCategory(ALL);
            setSelectedCondition(ALL);
            setSelectedBrands([]);
            setSelectedColors([]);
            setPriceRange([
              0,
              maxProductPrice,
            ]);
            setSearch("");
          }}
          className="
            mt-6
            w-full
            rounded-xl
            border
            border-slate-200
            bg-slate-100
            py-2.5
            text-sm
            font-bold
            text-slate-700
            transition
            hover:border-blue-600
            hover:bg-blue-600
            hover:text-white
          "
        >
          {text.reset}
        </button>
      </aside>

      {/* ================= PRODUCTS ================= */}
      <div className="min-w-0 flex-1">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-extrabold text-slate-900">
            {text.products}
          </h2>

          <span
            className="
              rounded-full
              border
              border-blue-100
              bg-blue-50
              px-3
              py-1
              text-xs
              font-bold
              text-blue-700
            "
          >
            {filteredProducts.length}
          </span>
        </div>

        {/* ================= PRODUCT GRID ================= */}
        <div
          className="
            grid
            grid-cols-2
            gap-4

            sm:grid-cols-3
            lg:grid-cols-4
            xl:grid-cols-5
            2xl:grid-cols-6
          "
        >
          {paginatedProducts.length > 0 ? (
            paginatedProducts.map((p) => (
            <div
              key={p.id}
              onClick={() =>
                navigate(
                  `/product/${encodeURIComponent(
                    p.brand
                  )}/${encodeURIComponent(p.model)}`
                )
              }
              className="
                group
                relative
                flex
                min-h-[310px]
                cursor-pointer
                flex-col
                overflow-hidden
                rounded-2xl
                border
                border-slate-200
                bg-white
                p-3
                shadow-[0_6px_20px_rgba(15,23,42,0.06)]
                transition
                duration-300
                hover:-translate-y-1
                hover:border-blue-200
                hover:shadow-[0_16px_35px_rgba(37,99,235,0.12)]
              "
            >
              {hasActiveOffer(p) && (
                  <div
                    className="
                      absolute
                      right-3
                      top-3
                      z-10
                      rounded-full
                      bg-red-600
                      px-2.5
                      py-1
                      text-xs
                      font-extrabold
                      text-white
                      shadow-lg
                    "
                  >
                    -{getDiscountPercent(p)}%
                  </div>
                )}

              <div
                className="
                  flex
                  h-36
                  items-center
                  justify-center
                  overflow-hidden
                  rounded-xl
                  bg-slate-50
                  p-2
                  md:h-40
                "
              >
                <img
                  src={getImage(p.images)}
                  alt={p.name || p.model || "Produkt"}
                  loading="lazy"
                  className="
                    h-full
                    w-full
                    object-contain
                    transition
                    duration-300
                    group-hover:scale-105
                  "
                />
              </div>

              <div className="flex flex-1 flex-col pt-3">
                {p.brand && (
                  <span
                    className="
                      mb-1
                      text-[9px]
                      font-extrabold
                      uppercase
                      tracking-[0.08em]
                      text-slate-400
                    "
                  >
                    {p.brand}
                  </span>
                )}

                <h3
                  className="
                    line-clamp-2
                    min-h-[38px]
                    text-xs
                    font-bold
                    leading-5
                    text-slate-900
                    md:text-sm
                  "
                >
                  {p.name}
                </h3>

                {hasActiveOffer(p) ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="text-sm font-bold text-slate-400 line-through">
                      {getNormalPrice(p).toFixed(2)} ALL
                    </span>

                    <span className="text-base font-extrabold text-red-600">
                      {getEffectivePrice(p).toFixed(2)} ALL
                    </span>
                  </div>
                ) : (
                  <p
                    className="
                      mt-2
                      text-base
                      font-extrabold
                      text-slate-900
                    "
                  >
                    {getEffectivePrice(p).toFixed(2)} ALL
                  </p>
                )}

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();

                    navigate(
                      `/product/${encodeURIComponent(
                        p.brand
                      )}/${encodeURIComponent(p.model)}`
                    );
                  }}
                  className="
                    mt-auto
                    w-full
                    rounded-xl
                    bg-green-600
                    py-2
                    text-xs
                    font-bold
                    text-white
                    transition
                    hover:bg-green-700
                    md:text-sm
                  "
                >
                  Bli Tani
                </button>
              </div>
            </div>
          ))
          ) : (
            <div className="col-span-full rounded-2xl border border-slate-200 bg-white px-6 py-14 text-center shadow-sm">
              <p className="text-sm font-semibold text-slate-600">
                {text.noResults}
              </p>
            </div>
          )}
        </div>

        {/* ================= PAGINATION ================= */}
        <div className="mt-10 flex items-center justify-center gap-3">
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() =>
              setCurrentPage((p) => Math.max(p - 1, 1))
            }
            className="
              flex
              h-10
              min-w-10
              items-center
              justify-center
              rounded-xl
              border
              border-slate-200
              bg-white
              px-3
              font-bold
              text-slate-700
              transition
              hover:border-blue-600
              hover:bg-blue-600
              hover:text-white
              disabled:cursor-not-allowed
              disabled:opacity-40
            "
          >
            ←
          </button>

          <span
            className="
              flex
              h-10
              items-center
              justify-center
              rounded-xl
              border
              border-slate-200
              bg-white
              px-4
              text-sm
              font-bold
              text-slate-700
            "
          >
            {currentPage} / {totalPages}
          </span>

          <button
            type="button"
            disabled={currentPage === totalPages}
            onClick={() =>
              setCurrentPage((p) =>
                Math.min(p + 1, totalPages)
              )
            }
            className="
              flex
              h-10
              min-w-10
              items-center
              justify-center
              rounded-xl
              border
              border-slate-200
              bg-white
              px-3
              font-bold
              text-slate-700
              transition
              hover:border-blue-600
              hover:bg-blue-600
              hover:text-white
              disabled:cursor-not-allowed
              disabled:opacity-40
            "
          >
            →
          </button>
        </div>
      </div>
    </div>
  </div>
);
};

export default Shop;